import type { ColDef, Column, GridApi, IRowNode, RowClassParams, ValueFormatterFunc, ValueGetterFunc } from 'ag-grid-enterprise'
import moment from 'moment'
import { uuidv7 } from 'posthog-js/lib/src/uuidv7'
import type { ComputedRef, Ref } from 'vue'
import DeltaValueRenderer from '~/components/dashboard/financialsTable/DeltaValueRenderer.vue'
import MenuItem from '~/components/dashboard/financialsTable/contextmenu/MenuItem.vue'
import GridDiffIcon from '~/components/financials/GridDiffIcon.vue'
import { useAppMachine } from '~/composables/useAppMachine'
import type { StcsCompActualsResValuesItem } from '~/protos/gen/stcs_comp_actuals_res_values.entity'
import { StcsFpnaTypeEnum, StcsFstmtEnum } from '~/protos/gen/stcs_enums.entity'
import type { StcsOrgFsdFlatItem } from '~/protos/gen/stcs_org_fsd_flat.entity'
import { formatCurrencyCtxValue, formatCurrencyValue, getRowDisplayName, valueGetter } from '~/services/commons.service'
import { useCtxStore } from '~/stores/ctx.store'
import type { createGridDiffStore } from '~/stores/griddiff.store'
import type { createGridMonthlyStore } from '~/stores/gridmonthly.store'
import type { createGridYearlyStore } from '~/stores/gridyearly.store'
import type { HideEmptyRowsFilter, LevelsFilter } from '~/types/app-settings'
import { FpnaDataType } from '~/types/gdata_col_list'
import { useNuxtApp } from '#imports'

/**
 * Calculates the fiscal year for a given date and fiscal year-end month.
 * @param date - The date to calculate the fiscal year for.
 * @param fyeMonth - The fiscal year-end month (1-12).
 * @returns The fiscal year number.
 */
export function getFiscalYear(date: Date, fyeMonth: number): number {
  const year = date.getFullYear()
  const month = date.getMonth() + 1 // getMonth() is 0-based

  // If current month is after fiscal year-end month, we're in the next fiscal year
  return month > fyeMonth ? year + 1 : year
}

export function getCompEndMonth(): number {
  const compId = useCtxStore().active_comp_id
  const currentCompData = useCtxStore().ref_comp_map[compId]
  return currentCompData?.ref_comp.data?.fpna_fye_month || 12
}

/**
 * Calculates the YTD fiscal year for a given date and fiscal year-end month.
 * @param currentDate - The current date.
 * @param fyeMonth - The fiscal year-end month (1-12).
 * @returns The YTD fiscal year number.
 */
export function getYtdFiscalYear(currentDate: Date, fyeMonth = getCompEndMonth()): number {
  const currentMonth = currentDate.getMonth() + 1
  const currentYear = currentDate.getFullYear()

  if (currentMonth > fyeMonth) {
    return currentYear + 1
    // biome-ignore lint/style/noUselessElse: <explanation>
  } else {
    return currentYear
  }
}

/**
 * Safely extracts the dateStr property from an AG-Grid column
 * @param column - The AG-Grid column object
 * @returns The dateStr value or undefined if not found
 */
export function getColumnDateStr(column: Column | null | undefined): string | undefined {
  if (!column) return undefined

  const colDef = column.getColDef() as StatycsColDef
  return colDef?.dateStr
}

export const financialsGridRowClassRules: Record<string, (params: RowClassParams) => boolean> = {
  'total-cml-delta': (params: RowClassParams) => params.data && params.data.rowClass === 'total-cml-delta',
  'sub-total-delta': (params: RowClassParams) => params.data && params.data.rowClass === 'sub-total-delta',
  'account-mapping-l1-delta': (params: RowClassParams) => params.data && params.data.rowClass === 'account-mapping-l1-delta',
  'account-mapping-l2-delta': (params: RowClassParams) => params.data && params.data.rowClass === 'account-mapping-l2-delta',
  'account-mapping-l3-delta': (params: RowClassParams) => params.data && params.data.rowClass === 'account-mapping-l3-delta',
  'account-item-delta': (params: RowClassParams) => params.data && params.data.rowClass === 'account-item-delta',
  'total-cml': (params: RowClassParams) => params.data && params.data.rowClass === 'total-cml',
  'total-cml-ctx-line-percent-parent': (params: RowClassParams) => params.data && params.data.rowClass === 'total-cml-ctx-line-percent-parent',
  'total-cml-ctx-line-percent-child': (params: RowClassParams) => params.data && params.data.rowClass === 'total-cml-ctx-line-percent-child',
  'total-ncml': (params: RowClassParams) => params.data && params.data.rowClass === 'total-ncml',
  'sub-total': (params: RowClassParams) => params.data && params.data.rowClass === 'sub-total',
  'account-mapping-l1': (params: RowClassParams) => params.data && params.data.rowClass === 'account-mapping-l1',
  'account-mapping-l2': (params: RowClassParams) => params.data && params.data.rowClass === 'account-mapping-l2',
  'account-mapping-l3': (params: RowClassParams) => params.data && params.data.rowClass === 'account-mapping-l3',
  'account-item': (params: RowClassParams) => params.data && params.data.rowClass === 'account-item',
  'add-new-row': (params: RowClassParams) => params.data && params.data.rowClass === 'add-new-row',
  'custom-planning-row': (params: RowClassParams) => params.data && params.data.rowClass === 'custom-planning-row',
}

export interface FinancialsGridGroupColData {
  headerName?: string
  headerClass: string
  children: FinancialsGridColData[]
  headerGroupComponent?: unknown
  headerGroupComponentParams?: {
    [key: string]: unknown
  }
}

export interface FinancialsGridColData {
  colId: string
  headerName: string
  dateStr: string
  headerClass: string
  cellClass?: string
  initialHide: boolean
  hide: boolean
  fstmtDataType: (typeof FpnaDataType)[keyof typeof FpnaDataType]
  headerStyle?: { [key: string]: string }
  planId?: string
  cellRenderer?: unknown
  // Optional ag-Grid value getter/formatter hooks used in some column strategies
  valueGetter?: ValueGetterFunc
  valueFormatter?: string | ValueFormatterFunc
  // Optional clipboard-specific header format (e.g., ISO 8601 date for Excel compatibility)
  clipboardHeaderName?: string
}

export type StatycsColDef = ColDef & FinancialsGridColData

export abstract class FinancialsGridColDataStrategy {
  readonly startDate: Date
  readonly endDate: Date
  readonly financialYearStart: { month: number; year: number }
  readonly actualsDate?: Date // Add optional actuals date parameter
  public readonly colData: FinancialsGridColData[] | FinancialsGridGroupColData[] // Property declaration
  public readonly fstmt?: StcsFstmtEnum
  public readonly colHeaderName?: string
  public readonly planId?: string
  public readonly col_1?: { is_actual?: boolean; range_date_str?: string; label?: string }
  public readonly col_2?: { is_actual?: boolean; range_date_str?: string; label?: string }
  // Minimal gridStore shape used by column strategies (valuesMap may be a Ref or plain object)
  public readonly valuesMap?: Record<string, StcsCompActualsResValuesItem> | undefined

  readonly $tolgee = useNuxtApp().$tolgee

  constructor(
    startDate: Date,
    endDate: Date,
    financialYearStart: { month: number; year: number },
    fstmt?: StcsFstmtEnum,
    colHeaderName?: string,
    actualsDate?: Date,
    planId?: string,
    col_1?: { is_actual?: boolean; range_date_str?: string; label?: string },
    col_2?: { is_actual?: boolean; range_date_str?: string; label?: string },
    valuesMap?: Record<string, StcsCompActualsResValuesItem> | undefined,
  ) {
    this.startDate = startDate
    this.endDate = endDate
    this.financialYearStart = financialYearStart
    this.actualsDate = actualsDate
    this.fstmt = fstmt
    this.colHeaderName = colHeaderName
    this.planId = planId
    this.col_1 = col_1
    this.col_2 = col_2
    this.valuesMap = valuesMap
    this.colData = this._generateColData()
  }

  // Abstract method that must be implemented by derived classes
  abstract _generateColData(): FinancialsGridColData[] | FinancialsGridGroupColData[]
}

export class MonthlyColData extends FinancialsGridColDataStrategy {
  override _generateColData(): FinancialsGridColData[] | FinancialsGridGroupColData[] {
    const locale: ComputedRef<string> = useAppMachine().locale as ComputedRef<string>
    const colData: FinancialsGridColData[] = []
    const currentDate = new Date(this.startDate)

    // Get the month and year from actuals date, or fall back to financial year start
    const currentMonthForYTD = this.actualsDate ? this.actualsDate.getMonth() + 1 : this.financialYearStart.month
    const currentYearForYTD = this.actualsDate ? this.actualsDate.getFullYear() : this.financialYearStart.year

    // Loop through each month until the currentDate exceeds the endDate
    while (currentDate <= this.endDate) {
      const month = currentDate.getMonth() + 1 // Months are 0-based in JS, so add 1
      const year = currentDate.getFullYear()

      // Create a unique column ID for each month
      const colId = `col_${year}_${month.toString().padStart(2, '0')}`

      // Format the month as "MMM YYYY" (e.g., "Jan 2024")
      const headerName = (() => {
        const formatted = currentDate.toLocaleString(locale.value, { month: 'short', year: 'numeric' }).replace(/\./g, '')
        const [month, year] = formatted.split(' ')
        return `${month.substring(0, 3)} ${year}`
      })()

      // Format the date as a string (e.g., "2024-01")
      const dateStr = `${year}-${month.toString().padStart(2, '0')}`

      // Format for clipboard as ISO 8601 with day set to 01 for Excel compatibility (e.g., "2024-01-01")
      const clipboardHeaderName = `${year}-${month.toString().padStart(2, '0')}-01`

      // Determine if this is an actual or planned column based on actuals date
      const isActualColumn = this.actualsDate ? currentDate <= this.actualsDate : true
      const isLastActualColumn = this.actualsDate ? currentDate.getTime() === this.actualsDate.getTime() : false

      // Push the data into colData
      colData.push({
        colId: colId,
        headerName: headerName,
        dateStr: dateStr,
        clipboardHeaderName: clipboardHeaderName,
        headerClass: month === currentMonthForYTD && year === currentYearForYTD ? 'header-actuals-ytd-begin' : 'header-actuals', // Default class, can be customized
        cellClass: isActualColumn ? (isLastActualColumn ? 'actual-col-editable' : 'actual-col') : 'planned-col', // Add cell classes for testing
        initialHide: false, // Default to showing initially
        hide: false, // Default to not hidden
        fstmtDataType: FpnaDataType.ACTUALS,
      })

      // Move to the next month
      currentDate.setMonth(currentDate.getMonth() + 1)
    }

    return colData
  }
}

export class YearlyColData extends FinancialsGridColDataStrategy {
  override _generateColData(): FinancialsGridColData[] {
    const colData: FinancialsGridColData[] = []

    // Calculate start and end fiscal years
    const startFiscalYear = getFiscalYear(this.startDate, this.financialYearStart.month)
    const planEndFiscalYear = getFiscalYear(this.endDate, this.financialYearStart.month)

    // Get the YTD fiscal year
    const currentYtdFiscalYear = this.actualsDate ? getYtdFiscalYear(this.actualsDate, this.financialYearStart.month) : this.financialYearStart.year

    // Include fiscal year of actuals date if beyond plan end
    const actualsFiscalYear = this.actualsDate ? getFiscalYear(this.actualsDate, this.financialYearStart.month) : planEndFiscalYear
    const endFiscalYear = Math.max(planEndFiscalYear, currentYtdFiscalYear, actualsFiscalYear)

    // Loop through each fiscal year
    for (let fiscalYear = startFiscalYear; fiscalYear <= endFiscalYear; fiscalYear++) {
      // Create a unique column ID for each fiscal year
      const colId = `col_${fiscalYear}`

      const headerName = fiscalYear.toString()

      // Format the date as a string (e.g., "2024")
      const dateStr = fiscalYear.toString()

      const isYTDYear = fiscalYear === currentYtdFiscalYear

      const isPlanSeleted = this.planId || useCtxStore().active_section === 'plan'

      // Push the data into colData
      colData.push({
        colId: colId,
        headerName: isYTDYear && !isPlanSeleted ? `YTD ${headerName}` : headerName,
        dateStr: dateStr,
        headerClass: fiscalYear === currentYtdFiscalYear ? 'header-actuals-ytd-begin' : 'header-actuals', // Default class, can be customized
        initialHide: false, // Default to showing initially
        hide: false, // Default to not hidden
        fstmtDataType: FpnaDataType.ACTUALS,
      })
    }

    return colData
  }
}

export class DiffColData extends FinancialsGridColDataStrategy {
  override _generateColData(): FinancialsGridGroupColData[] {
    const currentDate = new Date(this.startDate)
    const dateStr = currentDate.getFullYear().toString()
    // Get the YTD fiscal year
    const currentYtdFiscalYear = this.actualsDate ? getYtdFiscalYear(this.actualsDate, this.financialYearStart.month) : this.financialYearStart.year

    // Use label from col_1 if available (from predefined strategies), otherwise use custom colHeaderName, or fallback to YTD
    const col1HeaderName = this.col_1?.label === 'YTD' ? `YTD ${currentYtdFiscalYear}` : this.col_1?.label || this.colHeaderName || `YTD ${currentYtdFiscalYear}`
    const col2HeaderName = this.col_2?.label === 'YTD' ? `YTD ${currentYtdFiscalYear}` : this.col_2?.label || this.colHeaderName || `YTD ${currentYtdFiscalYear}`

    let headerNameAddition: string

    switch (this.fstmt) {
      case StcsFstmtEnum.SFSE_PNL:
        headerNameAddition = this.$tolgee.t('grid-comparison-output')
        break
      case StcsFstmtEnum.SFSE_BS:
        headerNameAddition = this.$tolgee.t('grid-comparison-total-assets')
        break
      case StcsFstmtEnum.SFSE_CF:
        headerNameAddition = this.$tolgee.t('grid-comparison-ebitda')
        break
      default:
        headerNameAddition = ''
    }

    // Determine headerClass based on col_1 and col_2 is_actual property
    const col1HeaderClass = this.col_1?.is_actual ? 'header-actuals-ytd-begin' : 'header-plan-ytd-begin'
    const col2HeaderClass = this.col_2?.is_actual ? 'header-actuals-ytd-begin' : 'header-plan-ytd-begin'

    return [
      {
        headerClass: 'header-ctx-line ag-left-aligned-header',
        children: [
          {
            colId: FpnaDataType.COMPARISON_ACTUALS,
            headerName: col1HeaderName,
            dateStr,
            headerClass: col1HeaderClass,
            initialHide: false, // Default to showing initially
            hide: false, // Default to not hidden
            fstmtDataType: FpnaDataType.COMPARISON_ACTUALS,
          },
          {
            colId: FpnaDataType.COMPARISON_PLAN,
            headerName: col2HeaderName,
            dateStr,
            headerClass: col2HeaderClass,
            initialHide: false, // Default to showing initially
            hide: false, // Default to not hidden
            fstmtDataType: FpnaDataType.COMPARISON_PLAN,
          },
          {
            colId: FpnaDataType.COMPARISON_DIFF,
            headerName: this.$tolgee.t('variance'),
            dateStr,
            headerClass: 'header-diff-begin',
            cellClass: 'left-column-border',
            initialHide: false, // Default to showing initially
            hide: false, // Default to not hidden
            fstmtDataType: FpnaDataType.COMPARISON_DIFF,
            valueGetter: (params) => valueGetter(params, this.valuesMap, true),
            valueFormatter: (params) => formatCurrencyValue(params.value),
            cellRenderer: DeltaValueRenderer,
          },
        ],
      },
      {
        headerClass: 'header-ctx-line ag-left-aligned-header left-column-gap has-icon',
        headerGroupComponent: GridDiffIcon,
        headerGroupComponentParams: {
          label: `${this.$tolgee.t('grid-comparison-in-perc-of')} ${headerNameAddition}`,
        },
        children: [
          {
            colId: FpnaDataType.COMPARISON_AIPO,
            headerName: col1HeaderName,
            dateStr,
            headerClass: `${col1HeaderClass} `,
            initialHide: false, // Default to showing initially
            hide: false, // Default to not hidden
            fstmtDataType: FpnaDataType.COMPARISON_AIPO,
          },
          {
            colId: FpnaDataType.COMPARISON_PIPO,
            headerName: col2HeaderName,
            dateStr,
            headerClass: `${col2HeaderClass} `,
            initialHide: false, // Default to showing initially
            hide: false, // Default to not hidden
            fstmtDataType: FpnaDataType.COMPARISON_PIPO,
          },
          {
            colId: FpnaDataType.COMPARISON_DIPO,
            headerName: this.$tolgee.t('variance'),
            dateStr,
            headerClass: 'header-diff-begin ',
            cellClass: 'left-column-border',
            initialHide: false, // Default to showing initially
            hide: false, // Default to not hidden
            fstmtDataType: FpnaDataType.COMPARISON_DIPO,
            valueGetter: (params) => valueGetter(params, this.valuesMap, true),
            valueFormatter: (params) => formatCurrencyCtxValue(params.value),
            cellRenderer: DeltaValueRenderer,
          },
        ],
      },
    ]
  }
}

/**
 * Helper method to find context line node
 */

export function getContextLineNode(node: IRowNode, gridApi: GridApi | null): IRowNode | null {
  // Only total field can have a context line
  const isTotal = node.data.fpna_type === StcsFpnaTypeEnum.SFT_TOTAL_CML || node.data.fpna_type === StcsFpnaTypeEnum.SFT_TOTAL_NCML

  if (!node || node.sourceRowIndex === undefined || !isTotal) {
    return null
  }

  const siblingNode = gridApi?.getRowNode(`${node.data.row_id}_ctx_line`)
  const isCxtLine = siblingNode?.data.fpna_type === StcsFpnaTypeEnum.SFT_CTX_LINE_GROWTH || siblingNode?.data.fpna_type === StcsFpnaTypeEnum.SFT_CTX_LINE_IPO
  if (siblingNode && isCxtLine) {
    return siblingNode
  }
  return null
}

export function findIndexWithPeriodBoundary(colData: (ColDef<FinancialsGridColData> | FinancialsGridColData)[]): { colId: string; position: 'auto' | 'start' | 'middle' | 'end' } {
  for (let i = 0; i < colData.length - 1; i++) {
    if (i === colData.length - 1) {
      break
    }
    const current = colData[i]
    const next = colData[i + 1]

    if ('fstmtDataType' in current && 'fstmtDataType' in next) {
      if ((current.fstmtDataType === FpnaDataType.ACTUALS && next.fstmtDataType === FpnaDataType.PLAN) || (!current.hide && next.hide)) {
        return { colId: current.colId, position: 'middle' }
      }
    }
  }
  const lastCol = colData[colData.length - 1]
  return { colId: 'fstmtDataType' in lastCol ? lastCol.colId : '', position: 'end' }
}

export { FpnaDataType }

export function findSmallestLeafIndex(targetNode: IRowNode): number {
  // Check if targetNode exists and has leaf children
  if (!targetNode.allLeafChildren) {
    return targetNode?.sourceRowIndex ?? -1
  }

  // Use reduce to find the smallest sourceRowIndex
  return targetNode.allLeafChildren.reduce((smallest, current) => {
    const currentIndex = current.sourceRowIndex
    if (currentIndex === undefined) {
      return smallest
    }
    if (smallest === -1) {
      return currentIndex
    }
    return currentIndex < smallest ? currentIndex : smallest
  }, -1)
}

export function findUpperBoundaryTotal(positionIndex: number, list: StcsOrgFsdFlatItem[], gridApi?: GridApi | null, excludedNode?: IRowNode): IRowNode | null {
  // Iterate backward from the target position to find the first total
  for (let i = positionIndex - 1; i >= 0; i--) {
    if (list[i].fpna_type === StcsFpnaTypeEnum.SFT_TOTAL_CML) {
      if (excludedNode && excludedNode.data.row_id === list[i].row_id) {
        continue
      }
      return gridApi?.getRowNode(list[i].row_id) ?? null
    }
  }

  return null // No upper boundary found
}

/**
 * Finds the lower boundary total (the total row below the given position)
 */
export function findLowerBoundaryTotal(positionIndex: number, list: StcsOrgFsdFlatItem[], gridApi?: GridApi | null, excludedNode?: IRowNode): IRowNode | null {
  // Iterate forward from the target position to find the first total
  for (let i = positionIndex; i < list.length; i++) {
    if (list[i].fpna_type === StcsFpnaTypeEnum.SFT_TOTAL_CML || list[i].fpna_type === StcsFpnaTypeEnum.SFT_TOTAL_NCML) {
      if (excludedNode && excludedNode.data.row_id === list[i].row_id) {
        continue
      }
      return gridApi?.getRowNode(list[i].row_id) ?? null
    }
  }

  return null // No lower boundary found
}

export function createExistsOptionBetweenTotalsChecker(node: IRowNode, list: StcsOrgFsdFlatItem[]): (itemId: string) => boolean {
  // Find indices of the current total and previous total
  const currentIndex = node.sourceRowIndex ?? -1

  // Find the previous total
  let prevTotalIndex = -1
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (list[i].fpna_type === StcsFpnaTypeEnum.SFT_TOTAL_CML) {
      prevTotalIndex = i
      break
    }
  }

  // Create a Set of row_ids between the totals for O(1) lookups
  const itemsBetweenTotals = new Set<string>()

  for (let i = prevTotalIndex + 1; i < currentIndex; i++) {
    const mirrorId = list[i].ref_id_mirror
    itemsBetweenTotals.add(mirrorId && list[i].row_id.includes(mirrorId) ? mirrorId : list[i].row_id)
  }

  return (itemId: string): boolean => {
    const level1Id = getLastIdByFpnaType(itemId, StcsFpnaTypeEnum.SFT_LEVEL_1)
    if (level1Id === null) {
      return false
    }
    for (const id of itemsBetweenTotals) {
      if (id.endsWith(level1Id)) {
        return true
      }
    }
    return false
  }
}

export function createExistsBetweenTotalsChecker(node: IRowNode, list: StcsOrgFsdFlatItem[]): (itemId: string) => boolean {
  // Find indices of the current total and previous total
  const currentIndex = node.sourceRowIndex !== null ? node.sourceRowIndex : -1

  // Find the previous total
  let prevTotalIndex = -1
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (list[i].fpna_type === StcsFpnaTypeEnum.SFT_TOTAL_CML) {
      prevTotalIndex = i
      break
    }
  }

  // Create a Set of row_ids between the totals for O(1) lookups
  const itemsBetweenTotals = new Set<string>()

  for (let i = prevTotalIndex + 1; i < currentIndex; i++) {
    const mirrorId = list[i].ref_id_mirror
    if (mirrorId && list[i].row_id.includes(mirrorId)) {
      itemsBetweenTotals.add(mirrorId)
    } else {
      itemsBetweenTotals.add(list[i].row_id)
    }
  }

  // Return a function that checks if an item exists between the totals
  return (itemId: string): boolean => {
    return itemsBetweenTotals.has(itemId)
  }
}

export function createChildNodeChecker(node: IRowNode, level1Id: string): boolean {
  if (!node.childrenAfterGroup) {
    return false
  }

  for (const childNode of node.childrenAfterGroup) {
    if (childNode.data.row_id.endsWith(level1Id)) {
      return true
    }
  }
  return false
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export function isDisplayNameDuplicate(draggedNode: IRowNode, targetNode: IRowNode, fsdStore: any): boolean {
  const draggedDisplayName: string | undefined = fsdStore.getDisplayNameBylevelId(draggedNode.data).name
  const targetNodeDisplayName: string | undefined = fsdStore.getDisplayNameBylevelId(targetNode.data).name

  return draggedDisplayName?.trim().toLowerCase() === targetNodeDisplayName?.trim().toLowerCase()
}

export function isAboveEBITDA(node: IRowNode, _gridApi: GridApi) {
  return node.data.row_id.includes('_total_ebitda')
}

export function isAboveEBITDADeprecated(node: IRowNode, gridApi: GridApi) {
  let ebitdaId = 'total_ebitda'

  // Use forEachNode instead of getRowNodes
  gridApi.forEachNode((rowNode: IRowNode) => {
    if (rowNode.data?.row_id?.endsWith(ebitdaId)) {
      ebitdaId = rowNode.data.row_id
    }
  })

  const upperboundNode = gridApi?.getRowNode(ebitdaId)
  const targetRowIndex: number = node.rowIndex !== null ? node.rowIndex : -1
  const upperboundRowIndex: number = upperboundNode?.rowIndex ?? -1
  return targetRowIndex <= upperboundRowIndex
}

export function getDuplicateItemsBelowEBITDA(targetId: string, gridApi: GridApi, list: StcsOrgFsdFlatItem[]) {
  return list.filter((item: StcsOrgFsdFlatItem) => {
    const rowNode = gridApi.getRowNode(item.row_id)
    return (item.ref_id_mirror === targetId || item.row_id === targetId) && rowNode && !isAboveEBITDA(rowNode, gridApi)
  })
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export function addFixedAssetsMirrorItems(node: IRowNode, rowId: string, fsdStore: any) {
  const targetPnlRowDataId = fsdStore.fstmtPnlList.find((item: StcsOrgFsdFlatItem) => item.row_id.endsWith('level_1_dna'))?.row_id
  const hashId = uuidv7()
  const pnl_level_2_dna_level_id = `level_2_dna_${hashId}`
  const rowIdL2PL = `${targetPnlRowDataId}_${pnl_level_2_dna_level_id}`
  const pnl_level_2_dna: StcsOrgFsdFlatItem = {
    fstmt_type: StcsFstmtEnum.SFSE_PNL,
    row_id: rowIdL2PL,
    level_id: pnl_level_2_dna_level_id,
    path_level: [targetPnlRowDataId, rowIdL2PL],
    fpna_type: StcsFpnaTypeEnum.SFT_LEVEL_2,
    rowClass: 'account-mapping-l2',
    ref_id_mirror: rowId,
    ref_id_prefix: 'fixed_assets_2',
    options: node.data.options,
    sum_ref: node.data.sum_ref,
  }
  fsdStore.addRow(pnl_level_2_dna, StcsFstmtEnum.SFSE_PNL, targetPnlRowDataId)

  const targetCfParentRowDataId = fsdStore.fstmtCfList.find((item: StcsOrgFsdFlatItem) => item.row_id.endsWith('total_cash_flow_from_investing'))?.row_id
  const cf_sub_total_level_id = `sub_total_investments_in_${hashId}`
  const cf_sub_total_row_id = `${targetCfParentRowDataId}_${cf_sub_total_level_id}`
  const cfRowL1Parent: StcsOrgFsdFlatItem = {
    fstmt_type: StcsFstmtEnum.SFSE_CF,
    row_id: cf_sub_total_row_id,
    level_id: cf_sub_total_level_id,
    path_level: [cf_sub_total_row_id],
    fpna_type: StcsFpnaTypeEnum.SFT_SUB_TOTAL,
    rowClass: 'sub-total',
    ref_id_mirror: rowId,
    ref_id_prefix: 'fixed_assets_1',
    options: {
      invert_sum_value: 1,
      skip_path_level_1: true,
    },
    sum_ref: node.data.sum_ref,
  }
  fsdStore.addRow(cfRowL1Parent, StcsFstmtEnum.SFSE_CF, targetCfParentRowDataId)

  // Use the BS row's level_2_id so the backend can match it against BS actuals/plan maps.
  // rowId ends with _level_1_fixed_assets_level_2_<uuid>; extract the level_2_<uuid> suffix.
  const cf_level_2_delta_level_id = rowId.split('_level_1_fixed_assets_').pop() ?? `level_2_${hashId}`
  const cf_level_2_delta_row_id = `${cf_sub_total_row_id}_level_1_fixed_assets_${cf_level_2_delta_level_id}`
  const cfRowL2ChildBS: StcsOrgFsdFlatItem = {
    fstmt_type: StcsFstmtEnum.SFSE_CF,
    row_id: cf_level_2_delta_row_id,
    level_id: cf_level_2_delta_level_id,
    path_level: [cf_sub_total_row_id, cf_level_2_delta_row_id],
    fpna_type: StcsFpnaTypeEnum.SFT_LEVEL_2,
    rowClass: 'account-mapping-l2',
    ref_id_mirror: rowId,
    ref_id_prefix: 'delta',
    options: {
      invert_sum_value: 1,
      bs_cf_delta: true,
      filter_on_zero_sum_period: false,
    },
  }
  fsdStore.addRow(cfRowL2ChildBS, StcsFstmtEnum.SFSE_CF, cfRowL1Parent.row_id)

  const cf_level_2_dna_level_id = `level_2_dna_${hashId}`
  const cf_level_2_dna_row_id = `${cf_sub_total_row_id}_level_1_fixed_assets_${cf_level_2_dna_level_id}`
  const cfRowFixedAssetsDna: StcsOrgFsdFlatItem = {
    fstmt_type: StcsFstmtEnum.SFSE_CF,
    row_id: cf_level_2_dna_row_id,
    level_id: cf_level_2_dna_level_id,
    path_level: [cf_sub_total_row_id, cf_level_2_dna_row_id],
    fpna_type: StcsFpnaTypeEnum.SFT_LEVEL_2,
    rowClass: 'account-mapping-l2',
    ref_id_mirror: rowIdL2PL,
    options: {
      invert_sum_value: 1,
      filter_on_zero_sum_period: false,
    },
    sum_ref: rowIdL2PL,
  }
  fsdStore.addRow(cfRowFixedAssetsDna, StcsFstmtEnum.SFSE_CF, cfRowL1Parent.row_id)
}

export function isPnlL1QualifiedForCFMirroring(targetId: string): boolean {
  return [
    'level_1_revenues',
    'level_1_output',
    'level_1_direct_costs',
    'level_1_operating_expenditure',
    'level_1_other_operating_income',
    'level_1_other_income_or_loss',
    'level_1_non_recurring_items',
    'level_1_net_interest',
    'level_1_tax_expense',
    'level_1_cash_n_cash_equivalents',
  ].some((suffix) => targetId.includes(suffix))
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export function addPnlL2MirrorItemToCF(node: IRowNode, mirrorRowId: string, parentCfId: string, fsdStore: any) {
  const hashId = uuidv7()
  const cf_level_id = `level_2_${hashId}`
  const cf_row_id = `${parentCfId}_${cf_level_id}`
  const cfRowData: StcsOrgFsdFlatItem = {
    ...node.data,
    row_id: cf_row_id,
    level_id: cf_level_id,
    path_level: [parentCfId, cf_row_id],
    fpna_type: StcsFpnaTypeEnum.SFT_LEVEL_2,
    ref_id_mirror: mirrorRowId,
    rowClass: 'account-mapping-l2',
    ref_id_prefix: undefined,
    sum_ref: mirrorRowId,
  }
  fsdStore.addRow(cfRowData, StcsFstmtEnum.SFSE_CF, parentCfId)
}

export function hasSameDisplayNameInGroup(node: IRowNode, displayName: string, displayNameMap: { [key: string]: string }): boolean {
  if (!displayName.trim()) {
    return false
  }

  const parent = node.parent
  if (!parent?.childrenAfterGroup) {
    return false
  }

  // Check siblings for duplicate names
  for (const sibling of parent.childrenAfterGroup) {
    // Skip the current node
    if (sibling.data.row_id === node.data.row_id) {
      continue
    }

    const siblingDisplayName = displayNameMap[sibling.data.level_id ?? sibling.data.row_id]
    if (siblingDisplayName?.trim().toLowerCase() === displayName.trim().toLowerCase()) {
      return true
    }
  }

  return false
}

/**
 * Check if the display name already exists in the duplicate group.
 * Returns true if the display name is found in the duplicate group.
 * @param gridApi
 * @param displayName
 * @param targetList
 * @param displayNameMap
 */
export function hasSameDisplayNameInDuplicateGroup(gridApi: GridApi, displayName: string, targetList: StcsOrgFsdFlatItem[], displayNameMap: { [key: string]: string }): boolean {
  if (!displayName.trim()) {
    return false
  }
  const hasDuplicate = targetList.some((parentItem) => {
    const rowNode = gridApi.getRowNode(parentItem.row_id)
    const hasDuplicateDisplayName = rowNode?.childrenAfterGroup?.some((childItem) => displayNameMap[childItem.data.level_id ?? childItem.data.row_id] === displayName)
    if (hasDuplicateDisplayName) {
      return true
    }
    return false
  })

  return hasDuplicate
}

/**
 * Disable grid scrolling
 */
export function disableGridScrolling(): void {
  const gridElement = document.querySelector('.ag-theme-alpine')
  const htmlElement = document.documentElement
  const bodyElement = document.body

  if (gridElement) {
    gridElement.classList.add('no-scroll')
  }

  htmlElement.classList.add('no-scroll')
  bodyElement.classList.add('no-scroll')
}

/**
 * Enable grid scrolling
 */
export function enableGridScrolling(): void {
  const gridElement = document.querySelector('.ag-theme-alpine')
  const htmlElement = document.documentElement
  const bodyElement = document.body

  if (gridElement) {
    gridElement.classList.remove('no-scroll')
  }

  htmlElement.classList.remove('no-scroll')
  bodyElement.classList.remove('no-scroll')
}

let activeContextMenu: HTMLElement | null = null

/**
 * Show the add context menu with options
 */
export function showContextMenu<
  T extends {
    icon?: string
    display_name?: string
    disable: boolean
  },
>(node: IRowNode, options: T[], openingTargetElement: string, onSelectCallback: (option: T) => void): void {
  // Remove any existing context menu
  if (activeContextMenu && document.body.contains(activeContextMenu)) {
    document.body.removeChild(activeContextMenu)
    activeContextMenu = null
  }

  const cellElement = document.querySelector(`[row-id="${node.data.row_id}"] ${openingTargetElement}`)
  if (!cellElement) {
    return
  }

  disableGridScrolling()

  // Create context menu element
  const menuElement = document.createElement('div')
  menuElement.className = 'context-menu rounded-lg shadow-lg text-white bg-gray-700 overflow-auto text-[13px] py-2'
  menuElement.style.position = 'fixed'
  menuElement.style.zIndex = '1000'

  // Track active context menu
  activeContextMenu = menuElement

  // Create menu items
  options.forEach((option) => {
    if (!option?.display_name) {
      return
    }

    const menuItem = document.createElement('div')
    menuItem.style.display = 'flex'
    menuItem.style.alignItems = 'center'
    if (option.icon) {
      const iconContainer = document.createElement('span')
      iconContainer.style.display = 'inline-block'
      iconContainer.style.verticalAlign = 'middle'
      iconContainer.innerHTML = option.icon

      // Add SVG styling directly
      const svg = iconContainer.querySelector('svg')
      if (svg) {
        svg.style.width = '100%'
        svg.style.height = '100%'
      }

      menuItem.appendChild(iconContainer)
      menuItem.appendChild(document.createTextNode(option.display_name))
    } else {
      menuItem.innerText = option.display_name
    }
    menuItem.style.padding = '8px 16px'
    menuItem.style.transition = 'background 0.2s'
    menuItem.style.opacity = option.disable ? '0.5' : '1'

    // Hover effect
    menuItem.onmouseover = () => {
      menuItem.style.background = 'rgba(33, 150, 243, 0.1)' // bg-blue-500 with 10% opacity
    }

    menuItem.onmouseout = () => {
      menuItem.style.background = '#374151' // Original bg-gray-700 color
    }

    // Click handler
    if (!option.disable) {
      menuItem.style.cursor = 'pointer'
      menuItem.onclick = () => {
        onSelectCallback(option)
        if (document.body.contains(menuElement)) {
          document.body.removeChild(menuElement)
        }
        activeContextMenu = null
        enableGridScrolling()
      }
    }

    menuElement.appendChild(menuItem)
  })

  // Position the menu - check if there's enough viewport space below
  const rect = cellElement.getBoundingClientRect()
  const viewportHeight = window.innerHeight

  // Add to body temporarily to get actual height
  document.body.appendChild(menuElement)
  const menuHeight = menuElement.offsetHeight

  // Position menu above or below based on available space
  if (rect.bottom + menuHeight > viewportHeight) {
    // Not enough space below, position above
    menuElement.style.top = `${Math.max(0, rect.top - menuHeight)}px`
  } else {
    // Enough space below, position as normal
    menuElement.style.top = `${rect.bottom}px`
  }

  menuElement.style.left = `${rect.left}px`

  // Close when clicking outside
  const closeMenu = (e: MouseEvent) => {
    if (!menuElement.contains(e.target as Node)) {
      if (document.body.contains(menuElement)) {
        document.body.removeChild(menuElement)
      }
      activeContextMenu = null
      enableGridScrolling()
      document.removeEventListener('click', closeMenu)
    }
  }

  // Delay adding the event listener to prevent immediate closing
  setTimeout(() => {
    document.addEventListener('click', closeMenu)
  }, 100)
}

// Define regex patterns at the top level scope
const LEVEL_1_WITH_BOUNDARY_REGEX = /(level_1_[\w-]+?)(?=_level_2|$)/
const LEVEL_1_REGEX = /(level_1_[\w-]+)/g
const LEVEL_2_REGEX = /(level_2_[\w-]+)(?=($|_))/g
const SUB_TOTAL_REGEX = /(sub_total_[\w-]+)/g
const LEVEL_OR_SUBTOTAL_REGEX = /_level_1_|_sub_total_/

/**
 * Get the last ID based on the fpnaType
 * @param compositeId
 * @param fpnaType
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: single purpose function
export function getLastIdByFpnaType(compositeId: string, fpnaType: StcsFpnaTypeEnum): string {
  if (!compositeId) {
    return ''
  }

  switch (fpnaType) {
    case StcsFpnaTypeEnum.SFT_TOTAL_CML:
    case StcsFpnaTypeEnum.SFT_TOTAL_NCML: {
      let relevantPart = compositeId
      const levelIdx = compositeId.search(LEVEL_OR_SUBTOTAL_REGEX)
      if (levelIdx !== -1) {
        relevantPart = compositeId.substring(0, levelIdx)
      }
      const segments = relevantPart.split('_')
      const totals: string[] = []
      for (let i = 0; i < segments.length; i++) {
        if (segments[i].startsWith('total')) {
          // Find the next index where a segment starts with total/level/sub or end of array
          let end = i + 1
          for (; end < segments.length; end++) {
            if (segments[end].startsWith('total') || segments[end].startsWith('level') || segments[end].startsWith('sub')) {
              break
            }
          }
          totals.push(segments.slice(i, end).join('_'))
          i = end - 1
        }
      }
      if (totals.length > 0) {
        return totals[totals.length - 1]
      }
      return compositeId
    }
    case StcsFpnaTypeEnum.SFT_SUB_TOTAL: {
      const matches = [...compositeId.matchAll(SUB_TOTAL_REGEX)].map((m) => m[1])
      if (matches && matches.length > 0) {
        return matches[matches.length - 1]
      }
      return compositeId
    }
    case StcsFpnaTypeEnum.SFT_LEVEL_1: {
      // Find level_1 pattern but make sure it stops before level_2 if present
      const level1Match = compositeId.match(LEVEL_1_WITH_BOUNDARY_REGEX)
      if (level1Match) {
        return level1Match[1]
      }
      const matches = [...compositeId.matchAll(LEVEL_1_REGEX)].map((m) => m[1])
      if (matches && matches.length > 0) {
        return matches[matches.length - 1]
      }
      return compositeId
    }
    case StcsFpnaTypeEnum.SFT_LEVEL_2: {
      const matches = [...compositeId.matchAll(LEVEL_2_REGEX)].map((m) => m[1])
      if (matches && matches.length > 0) {
        return matches[matches.length - 1]
      }
      return compositeId
    }
    default:
      return compositeId
  }
}

/**
 * Get the prefix of a string until the first occurrence of 'level_1'
 * @param input
 * @param marker
 */
export function getPrefixUntilMarker(input: string, marker: string): string {
  const idx = input.indexOf(marker)
  return idx === -1 ? input : input.slice(0, idx + marker.length)
}

export function copyWithGroupHeaders(gridStore: ReturnType<typeof createGridMonthlyStore> | ReturnType<typeof createGridYearlyStore> | ReturnType<typeof createGridDiffStore>) {
  const { $tolgee } = useNuxtApp()
  return {
    name: $tolgee.t('grid-copy-with-headers'),
    icon: "<i class='bx bx-copy'></i>",
    suppressCloseOnSelect: true,
    action: () => {
      gridStore.copyWithGroupHeaders()
    },
  }
}

export function copy(gridStore: ReturnType<typeof createGridMonthlyStore> | ReturnType<typeof createGridYearlyStore> | ReturnType<typeof createGridDiffStore>) {
  const { $tolgee } = useNuxtApp()
  return {
    name: $tolgee.t('grid-copy'),
    suppressCloseOnSelect: true,
    icon: "<i class='bx bx-copy'></i>",
    action: () => {
      gridStore.copy()
    },
  }
}

export function expandAllMenuItem(gridStore: ReturnType<typeof createGridMonthlyStore> | ReturnType<typeof createGridYearlyStore> | ReturnType<typeof createGridDiffStore>, onAfterExpand?: () => void) {
  const { $tolgee } = useNuxtApp()
  return {
    name: $tolgee.t('grid-expand-all'),
    suppressCloseOnSelect: true,
    cssClasses: ['expand-all'],
    dataTestId: 'expand-all',
    action: () => {
      gridStore.expandAllRows()
      gridStore.clearSelection()
      // Call the callback which will save the "expand all" state efficiently
      onAfterExpand?.()
    },
  }
}

export function collapseAllMenuItem(gridStore: ReturnType<typeof createGridMonthlyStore> | ReturnType<typeof createGridYearlyStore> | ReturnType<typeof createGridDiffStore>, onAfterCollapse?: () => void) {
  const { $tolgee } = useNuxtApp()
  return {
    name: $tolgee.t('grid-collapse-all'),
    suppressCloseOnSelect: true,
    cssClasses: ['collapse-all'],
    dataTestId: 'collapse-all',
    action: () => {
      gridStore.collapseAllRows()
      gridStore.clearSelection()
      // Call the callback which will save the "collapse all" state efficiently
      onAfterCollapse?.()
    },
  }
}

export function hideContextLineRows(gridStore: ReturnType<typeof createGridMonthlyStore> | ReturnType<typeof createGridYearlyStore> | ReturnType<typeof createGridDiffStore>, showContextLine: Ref<boolean>, disabled = false) {
  const { $tolgee } = useNuxtApp()

  return {
    name: $tolgee.t('grid-hide-context-line-rows'),
    disabled,
    suppressCloseOnSelect: true,
    menuItem: MenuItem,
    menuItemParams: {
      getChecked: () => showContextLine.value,
      onToggle: (checked: boolean) => {
        showContextLine.value = checked
        // Just trigger a simple filter refresh, no manual row height manipulation
        setTimeout(() => {
          gridStore.onFilterChanged()
        }, 0)
      },
      dataTestId: 'show-growth-percent',
    },
  }
}

export function showLevelsMenuItem(showLevels: Ref<LevelsFilter>, isPlanningMonthlyView?: boolean, hideTagsAndAccounts?: boolean, disabled = false, fstmt?: StcsFstmtEnum) {
  const { $tolgee } = useNuxtApp()

  const menuItems = [
    // Subtotals are not applicable to P&L
    ...(fstmt !== StcsFstmtEnum.SFSE_PNL
      ? [
          {
            name: $tolgee.t('grid-show-sub-totals-option'),
            suppressCloseOnSelect: true,
            menuItem: MenuItem,
            menuItemParams: {
              getChecked: () => showLevels.value.subTotals,
              onToggle: (checked: boolean) => {
                showLevels.value = {
                  ...showLevels.value,
                  subTotals: checked,
                }
              },
              dataTestId: 'sub-totals',
            },
            disabled: isPlanningMonthlyView,
          },
        ]
      : []),
    {
      name: $tolgee.t('grid-show-level-1-option'),
      suppressCloseOnSelect: true,
      menuItem: MenuItem,
      menuItemParams: {
        getChecked: () => showLevels.value.level1,
        onToggle: (checked: boolean) => {
          showLevels.value = {
            ...showLevels.value,
            level1: checked,
          }
        },
        dataTestId: 'level-1',
      },
      disabled: isPlanningMonthlyView,
    },
    {
      name: $tolgee.t('grid-show-level-2-option'),
      suppressCloseOnSelect: true,
      menuItem: MenuItem,
      menuItemParams: {
        getChecked: () => showLevels.value.level2,
        onToggle: (checked: boolean) => {
          showLevels.value = {
            ...showLevels.value,
            level2: checked,
          }
        },
        dataTestId: 'level-2',
      },
      disabled: isPlanningMonthlyView,
    },
  ]

  if (!hideTagsAndAccounts) {
    menuItems.push(
      {
        name: $tolgee.t('grid-show-tags-option'),
        suppressCloseOnSelect: true,
        menuItem: MenuItem,
        menuItemParams: {
          getChecked: () => showLevels.value.tags,
          onToggle: (checked: boolean) => {
            showLevels.value = {
              ...showLevels.value,
              tags: checked,
            }
          },
          dataTestId: 'tags',
        },
        disabled: false,
      },
      {
        name: $tolgee.t('grid-show-accounts-option'),
        suppressCloseOnSelect: true,
        menuItem: MenuItem,
        menuItemParams: {
          getChecked: () => showLevels.value.accounts,
          onToggle: (checked: boolean) => {
            showLevels.value = {
              ...showLevels.value,
              accounts: checked,
            }
          },
          dataTestId: 'accounts',
        },
        disabled: false,
      },
    )
  }

  return {
    name: $tolgee.t('grid-show-levels-menu'),
    cssClasses: ['show-levels-menu-item'],
    disabled,
    subMenu: menuItems,
  }
}

export function hideEmptyRowsMenuItem(hideEmptyRows: Ref<HideEmptyRowsFilter>, disabled = false, fstmt?: StcsFstmtEnum, fstmtList?: StcsOrgFsdFlatItem[]) {
  const { $tolgee } = useNuxtApp()

  return {
    name: $tolgee.t('grid-hide-empty-rows-menu'),
    cssClasses: ['hide-empty-rows-menu-item'],
    disabled,
    subMenu: [
      // Subtotals are not applicable to P&L
      ...(fstmt !== StcsFstmtEnum.SFSE_PNL
        ? [
            {
              name: $tolgee.t('grid-show-sub-totals-option'),
              suppressCloseOnSelect: true,
              menuItem: MenuItem,
              menuItemParams: {
                getChecked: () => hideEmptyRows.value.subTotals,
                onToggle: (checked: boolean) => {
                  hideEmptyRows.value = {
                    ...hideEmptyRows.value,
                    subTotals: checked,
                  }
                },
                dataTestId: 'hide-empty-sub-totals',
              },
            },
          ]
        : []),
      {
        name: $tolgee.t('grid-show-level-1-option'),
        suppressCloseOnSelect: true,
        menuItem: MenuItem,
        menuItemParams: {
          getChecked: () => hideEmptyRows.value.level1,
          onToggle: (checked: boolean) => {
            hideEmptyRows.value = {
              ...hideEmptyRows.value,
              level1: checked,
            }
          },
          dataTestId: 'hide-empty-level-1',
        },
      },
      {
        name: $tolgee.t('grid-show-level-2-option'),
        suppressCloseOnSelect: true,
        menuItem: MenuItem,
        menuItemParams: {
          getChecked: () => hideEmptyRows.value.level2,
          onToggle: (checked: boolean) => {
            hideEmptyRows.value = {
              ...hideEmptyRows.value,
              level2: checked,
            }
          },
          dataTestId: 'hide-empty-level-2',
        },
      },
      {
        name: $tolgee.t('grid-show-tags-option'),
        suppressCloseOnSelect: true,
        menuItem: MenuItem,
        menuItemParams: {
          getChecked: () => hideEmptyRows.value.tags,
          onToggle: (checked: boolean) => {
            hideEmptyRows.value = {
              ...hideEmptyRows.value,
              tags: checked,
            }
          },
          dataTestId: 'hide-empty-tags',
        },
      },
      {
        name: $tolgee.t('grid-show-accounts-option'),
        suppressCloseOnSelect: true,
        menuItem: MenuItem,
        menuItemParams: {
          getChecked: () => hideEmptyRows.value.accounts,
          onToggle: (checked: boolean) => {
            hideEmptyRows.value = {
              ...hideEmptyRows.value,
              accounts: checked,
            }
          },
          dataTestId: 'hide-empty-accounts',
        },
      },
      // "Other Cash Flow" option — only visible on the CF tab
      ...(fstmt === StcsFstmtEnum.SFSE_CF
        ? [
            {
              name: (() => {
                const otherCfRow = fstmtList?.find((r) => r.level_id === 'total_other_cash_flow')
                return otherCfRow ? getRowDisplayName(otherCfRow).name : $tolgee.t('grid-hide-empty-other-cash-flow')
              })(),
              suppressCloseOnSelect: true,
              menuItem: MenuItem,
              menuItemParams: {
                getChecked: () => hideEmptyRows.value.otherCashFlow ?? true,
                onToggle: (checked: boolean) => {
                  hideEmptyRows.value = {
                    ...hideEmptyRows.value,
                    otherCashFlow: checked,
                  }
                },
                dataTestId: 'hide-empty-other-cash-flow',
              },
            },
          ]
        : []),
    ],
  }
}

export function hideOldYearsMenuItem(hideOldYears: Ref<boolean>) {
  const { $tolgee } = useNuxtApp()

  return {
    name: $tolgee.t('grid-hide-years-older-than-3'),
    suppressCloseOnSelect: true,
    menuItem: MenuItem,
    menuItemParams: {
      getChecked: () => hideOldYears.value,
      onToggle: (checked: boolean) => {
        hideOldYears.value = checked
      },
    },
  }
}

export function hideOldMonthsMenuItem(hideOldMonths: Ref<boolean>, _has_plan_data: Ref<boolean>, disabled = false) {
  const { $tolgee } = useNuxtApp()

  return {
    name: $tolgee.t('grid-hide-months-older-than-latest-year'),
    disabled,
    suppressCloseOnSelect: true,
    menuItem: MenuItem,
    menuItemParams: {
      getChecked: () => hideOldMonths.value,
      onToggle: (checked: boolean) => {
        hideOldMonths.value = checked
      },
      dataTestId: 'hide-old-months',
    },
  }
}

/**
 * Utility function to check if a column is an actual column
 */
export function isActualColumn(colId: string | null, gridApi: GridApi | null): boolean {
  if (!colId) return false
  if (!gridApi) return false
  const col = gridApi.getColumn(colId)
  if (!col) return false
  const headerClass = col?.getColDef()?.headerClass
  if (typeof headerClass === 'string') {
    return headerClass.includes('header-actuals')
  }
  if (Array.isArray(headerClass)) {
    return headerClass.includes('header-actuals')
  }
  // If headerClass is a function or undefined, treat as not actuals column
  return false
}

/**
 * Get all actual columns from the grid
 */
export function getAllActualColumns(gridApi: GridApi | null): Column[] {
  if (!gridApi) return []
  const allCols = gridApi.getAllDisplayedColumns() || []
  return allCols.filter((c: Column) => isActualColumn(c.getColId(), gridApi))
}

/**
 * Get the target column (first of the last three actual columns) for formula display
 * This is commonly used in formula-based planning to determine which column should show formula chips
 */
export function getFormulaTargetColumn(gridApi: GridApi | null): Column | undefined {
  const actualCols = getAllActualColumns(gridApi)
  if (actualCols.length === 0) return undefined

  // Get the target column (first of the last three)
  const idx = Math.max(0, actualCols.length - 3)
  return actualCols[idx]
}

/**
 * Check if the current column is the formula target column
 */
export function isFormulaTargetColumn(currentColId: string | null, gridApi: GridApi | null): boolean {
  if (!currentColId) return false

  const targetColumn = getFormulaTargetColumn(gridApi)
  if (!targetColumn) return false

  return currentColId === targetColumn.getColId()
}

export function buildPathTree<T extends { path_level: string[] }>(rows: T[]): TreeNode<T>[] {
  const root: Record<string, TreeNode<T>> = {}
  for (const r of rows) {
    let here = root

    r.path_level.forEach((segment, idx) => {
      if (!here[segment]) {
        here[segment] = { label: segment, children: [] }
      }
      if (idx < r.path_level.length - 1) {
        if (!(here[segment] as TreeNodeWithMap<T>)._map) {
          ;(here[segment] as TreeNodeWithMap<T>)._map = {}
          here[segment].children = []
        }
        here = (here[segment] as TreeNodeWithMap<T>)._map
      } else {
        here[segment].data = r
      }
    })
  }
  function toArray(map: Record<string, TreeNode<T>>): TreeNode<T>[] {
    return Object.values(map).map((n) => ({
      ...n,
      children: toArray((n as TreeNodeWithMap<T>)._map ?? {}),
    }))
  }
  return toArray(root)
}

/**
 * Aggregate monthly data into fiscal years based on cutoff dates and fiscal year-end month.
    - @param rawMap: A record mapping date strings to numerical values.
    - @param cutoff: The cutoff date for aggregation.
    - @param fyeMonth: The fiscal year-end month (1-12).
    - @param nextCutoff: Optional next cutoff date for additional aggregation.
    - @returns A record mapping fiscal years to aggregated numerical values.
*/
export function aggregateYearly(rawMap: Record<string, number>, cutoff: Date, fyeMonth: number, nextCutoff?: Date): Record<number, number> {
  const buckets: Record<number, number> = {}

  Object.entries(rawMap)
    .filter(([ds]) => moment(ds).isSameOrBefore(cutoff, 'day'))
    .forEach(([ds, v]) => {
      const date = new Date(ds)
      const fiscalYear = getFiscalYear(date, fyeMonth)
      buckets[fiscalYear] = (buckets[fiscalYear] || 0) + v
    })

  if (nextCutoff) {
    Object.entries(rawMap)
      .filter(([ds]) => {
        const d = new Date(ds)
        return d > cutoff && moment(d).isSameOrBefore(nextCutoff, 'day')
      })
      .forEach(([ds, v]) => {
        const date = new Date(ds)
        const fiscalYear = getFiscalYear(date, fyeMonth)
        buckets[fiscalYear] = (buckets[fiscalYear] || 0) + v
      })
  }

  return buckets
}

/**
 * Aggregate balance sheet data into fiscal years.
 * @param data - A record mapping date strings (YYYY-MM-DD) to numeric values.
 * @param cutoff - The cutoff date for aggregation.
 * @param fyeMonth - The fiscal year-end month (1-12).
 * @param actStartDate - Optional actuals start date to include all fiscal years from this date.
 * @returns A record mapping fiscal years to aggregated numeric values.
 */
export function aggregateBsYearly(data: Record<string, number>, cutoff: Date, fyeMonth: number, actStartDate?: Date): Record<number, number> {
  const latestByYear: Record<number, string> = {}
  const startDate = moment(actStartDate)
  const endDate = moment(cutoff)

  if (actStartDate) {
    for (let year = startDate.year(); year <= endDate.year(); year++) {
      latestByYear[year] = moment(`${year}-${fyeMonth}`, 'YYYY-M').format('YYYY-MM')
    }
  } else {
    Object.entries(data)
      .filter(([ds]) => moment(ds).isSameOrBefore(cutoff, 'day'))
      .forEach(([ds]) => {
        const date = new Date(ds)
        const fiscalYear = getFiscalYear(date, fyeMonth)
        if (!latestByYear[fiscalYear] || moment(ds).isAfter(moment(latestByYear[fiscalYear]), 'day')) {
          latestByYear[fiscalYear] = ds
        }
      })
  }

  const result: Record<number, number> = {}
  Object.entries(latestByYear).forEach(([yrStr, ds]) => {
    const yr = Number(yrStr)
    result[yr] = data[ds]
  })

  return result
}

export function getHeaderClass(colDef: ColDef | undefined): string {
  if (!colDef) return ''
  return (colDef.headerClass as string) || ''
}

/**
 * Clean invisible characters from text for clipboard operations
 * Removes zero-width spaces, non-breaking spaces, and other invisible unicode characters
 * @param text - The text to clean
 * @returns Cleaned text with invisible characters removed
 */
export function cleanInvisibleChars(text: string | null | undefined): string {
  if (!text) return ''

  // Convert to string if not already
  const str = String(text)

  return (
    str
      // Remove zero-width spaces and joiners
      // biome-ignore lint/suspicious/noMisleadingCharacterClass: intentionally removing zero-width joiners
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      // Remove other invisible/control characters except common whitespace (space, tab, newline)
      // biome-ignore lint/suspicious/noControlCharactersInRegex: intentionally removing control characters for clipboard cleanup
      .replace(/[\u0000-\u0008\u000B-\u001F\u007F-\u009F]/g, '')
      // Remove paragraph separator and line separator (common in AG Grid)
      .replace(/[\u2028\u2029]/g, '')
      // Remove directional formatting characters (LTR/RTL marks, overrides)
      .replace(/[\u200E\u200F\u202A-\u202E]/g, '')
      // Remove soft hyphens
      .replace(/\u00AD/g, '')
      // Normalize non-breaking spaces to regular spaces
      .replace(/\u00A0/g, ' ')
      // Normalize thin space, hair space, and other narrow spaces
      .replace(/[\u2000-\u200A\u202F\u205F\u3000]/g, ' ')
      // Remove object replacement character
      .replace(/\uFFFC/g, '')
      // Remove variation selectors
      // biome-ignore lint/suspicious/noMisleadingCharacterClass: intentionally removing variation selectors for clipboard cleanup
      .replace(/[\uFE00-\uFE0F]/g, '')
  )
}

/**
 * Get the appropriate header name for clipboard operations
 * Returns clipboardHeaderName if available (Excel-compatible ISO 8601 format for monthly data),
 * otherwise falls back to the display headerName
 * @param colDef - The column definition
 * @returns Header name for clipboard, cleaned of invisible characters
 */
export function getClipboardHeaderName(colDef: ColDef | undefined): string {
  if (!colDef) return ''

  const statycsColDef = colDef as StatycsColDef
  const headerName = statycsColDef?.clipboardHeaderName || statycsColDef?.headerName || ''

  return cleanInvisibleChars(headerName)
}
